/**
 * Detail Book / workstation — Lab `workstation.js` API set via ForGroup.
 *
 * - `getTotalBooks1ForGroup` — particular books (runners + group NE)
 * - `getBooksForBackendForGroup` — market-scoped event group (metadata / fallback)
 * - `getMemberNetExposureForGroup` — headline NE for the market
 * - `getBetDetailsForWorkstationForGroup` — bet ticker
 * - `getBookForDownline` — family-tree expand (immediate children)
 *
 * Demo / no bet URL → fixture book (same as other console tabs).
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  betFetch,
  hasLiveApi,
  hasLiveBet,
} from '@/api/client'
import { fetchAggregateMe } from '@/api/aggregateMe'
import { useSession } from '@/api/hooks/shared/useSession'
import { keys } from '@/api/hooks/shared/keys'
import type { BookEventGroupBackend } from '@/api/map/books'
import { bookScopeRoots } from '@/api/hooks/net-exposure/useBooks'
import {
  assembleWorkstationBook,
  hasDownlineBookPosition,
  marketFromBooksGroups,
  treeNodeFromDownlineBook,
  type BetDetailsForWorkstationRequest,
  type BetDetailsForWorkstationResponse,
  type GetLadderResponse,
  type MemberNetExposureResponse,
  type TotalBooks1Request,
  type TotalBooks1Response,
} from '@/api/map/workstation'
import { applyLiveRatesToBook, fancyRatesFromBets, mergeLiveRatePayload } from '@/api/map/rates'
import { listChildren } from '@/api/hooks/downline/useChildren'
import { ROLE_LABEL, type Role } from '@/data/types'
import type { BookType } from '@/features/net-exposure/fixture'
import {
  defaultBetDateRange,
  getWorkstationBook,
  type WorkstationBook,
  type WorkstationMarketType,
  type WorkstationRunner,
  type WorkstationTreeNode,
} from '@/features/net-exposure/workstation-fixture'
import { BOOKS_POLL_MS } from './useBooks'
import { useMarketRatesStream } from './useMarketRatesStream'
import {
  buildFancyPtLadderBody,
  fancyPtLadderRoute,
  fancyPtLadderUsesTotal,
} from './fancyPtLadder'

/** Lab bet ticker polls every 3s (workstation.js ~2–5s). */
export const BET_TICKER_POLL_MS = 3_000

export type WorkstationQuery = {
  marketId: string
  selectedType: BookType
  /** Search-param fallbacks. */
  eventName?: string
  marketName?: string
  type?: WorkstationMarketType
  /** Bet ticker filters (Lab getBetList). */
  page?: number
  maxRow?: number
  fromDate?: string
  toDate?: string
  userName?: string | string[]
  currencyType?: string
}

export type WorkstationBetFilters = Pick<
  WorkstationQuery,
  'page' | 'maxRow' | 'fromDate' | 'toDate' | 'userName' | 'currencyType'
>

function labLevelLabel(role: Role | undefined): string {
  if (!role) return 'Master'
  return ROLE_LABEL[role]
}

/** Lab Aggregate `getTotalBooks1ForGroup` — whole memberList Detail Book. */
async function loadTotalBooks1(
  query: WorkstationQuery,
  user: string,
  accessLevel: string,
  username: string | undefined,
): Promise<TotalBooks1Response> {
  const body: TotalBooks1Request = {
    user,
    marketId: query.marketId,
    loggedInMember: user,
    username: username || undefined,
    accessLevel,
    selectedType: query.selectedType,
    page: 1,
  }
  return betFetch<TotalBooks1Response>('/v1/book/getTotalBooks1ForGroup', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

async function loadBooksForMarket(
  query: WorkstationQuery,
  _user: string,
  _accessLevel: string,
): Promise<BookEventGroupBackend[]> {
  return betFetch<BookEventGroupBackend[]>('/v1/book/getBooksForBackendForGroup', {
    method: 'POST',
    body: JSON.stringify({
      selectedType: query.selectedType,
      filter: {
        marketId: query.marketId,
        eventname: 'All',
        bookmakerSessionFlag: 'all',
      },
    }),
  })
}

async function loadMemberNetExposure(
  marketId: string,
  _user: string,
): Promise<MemberNetExposureResponse> {
  return betFetch<MemberNetExposureResponse>('/v1/book/getMemberNetExposureForGroup', {
    method: 'POST',
    body: JSON.stringify({ marketId }),
  })
}

async function loadBetDetails(
  query: WorkstationQuery,
  user: string,
  accessLevel: string,
): Promise<BetDetailsForWorkstationResponse> {
  const defaults = defaultBetDateRange()
  const body: BetDetailsForWorkstationRequest = {
    user,
    marketId: query.marketId,
    accessLevel,
    page: query.page ?? 1,
    maxRow: query.maxRow ?? 100,
    fromDate: query.fromDate || defaults.fromDate,
    toDate: query.toDate || defaults.toDate,
    currencyType: query.currencyType || 'All',
  }
  if (query.userName != null && query.userName !== '') body.userName = query.userName
  return betFetch<BetDetailsForWorkstationResponse>(
    '/v1/bets/getBetDetailsForWorkstationForGroup',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

type DownlineBookInput = {
  parentUserId: string
  marketId: string
  selectedType: BookType
  runners: WorkstationRunner[]
  marketType: WorkstationMarketType
}

export type GetBookForDownlineRequest = {
  marketId: string
  selectedType?: BookType | string
  parentUserId?: string
  children: Array<{
    user: string
    accessLevel?: string
    username?: string
  }>
}

/**
 * Lab `showDownLine` → `getImmediateChildren` + `Book/getBookForDownline`.
 * Direct children only (not full subtree). One bet-service call for all kids.
 */
export async function loadDownlineParticularBooks(
  input: DownlineBookInput,
): Promise<WorkstationTreeNode[]> {
  const kids = await listChildren(input.parentUserId)
  if (!kids.length) return []

  const byId = new Map(kids.map((k) => [k.id, k]))
  const res = await betFetch<TotalBooks1Response>('/v1/book/getBookForDownline', {
    method: 'POST',
    body: JSON.stringify({
      parentUserId: input.parentUserId,
      marketId: input.marketId,
      selectedType: input.selectedType,
      children: kids.map((child) => ({
        user: child.id,
        accessLevel: ROLE_LABEL[child.role],
        username: child.username || undefined,
      })),
    } satisfies GetBookForDownlineRequest),
  })

  const nodes: WorkstationTreeNode[] = []
  for (const row of res.book ?? []) {
    if (!hasDownlineBookPosition(row)) continue
    const child = byId.get(String(row.user ?? ''))
    nodes.push(
      treeNodeFromDownlineBook(row, input.runners, input.marketType, {
        username: row.username || child?.username,
        level: child?.role === 'player' ? 'player' : 'agent',
        accessLevel: child ? ROLE_LABEL[child.role] : undefined,
      }),
    )
  }
  return nodes.sort((a, b) => a.username.localeCompare(b.username))
}

/**
 * Aggregate Detail Book — ForGroup APIs sum the whole memberList.
 * First root is only a display / ladder / downline parent id (Lab tree chrome).
 * Demo / no live bet → fixtures via `useWorkstationBook` (`live: false`).
 */
function useLiveIdentity() {
  const { data: session } = useSession()
  const meQ = useQuery({
    queryKey: ['aggregate-me', 'workstation-book-actor'],
    queryFn: fetchAggregateMe,
    enabled: hasLiveApi(),
    staleTime: 60_000,
  })
  const roots = bookScopeRoots(meQ.data?.caller)
  const primary = roots[0]
  const groupId = meQ.data?.caller.aggregateGroupId?.trim() || ''
  const user = primary?.user || groupId
  const accessLevel = primary?.level ?? labLevelLabel(session?.account.role)
  const username =
    meQ.data?.caller.groupName ??
    meQ.data?.caller.preferredUsername ??
    meQ.data?.caller.email ??
    session?.account.username
  const live = Boolean(session && hasLiveApi() && hasLiveBet() && user)
  return { session, user, accessLevel, username, live, role: session?.account.role }
}

/** Lab Aggregate Detail Book → `POST /v1/book/getTotalBooks1ForGroup`. */
export function useTotalBooks1(query: WorkstationQuery) {
  const { session, user, accessLevel, username, live } = useLiveIdentity()
  return useQuery({
    queryKey: keys.workstationTotalBooks({
      marketId: query.marketId,
      selectedType: query.selectedType,
      user,
      level: accessLevel,
    }),
    queryFn: () => loadTotalBooks1(query, user!, accessLevel, username),
    enabled: Boolean(session && live && query.marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Lab family-icon expand — downline books for one parent on this market.
 * Polls while open (Lab `timeoutChildren` refresh on book change).
 */
export function useDownlineParticularBooks(input: {
  parentUserId: string
  marketId: string
  selectedType: BookType
  runners: WorkstationRunner[]
  marketType: WorkstationMarketType
  enabled: boolean
}) {
  return useQuery({
    queryKey: keys.workstationDownlineBooks({
      parentUserId: input.parentUserId,
      marketId: input.marketId,
      selectedType: input.selectedType,
    }),
    queryFn: () =>
      loadDownlineParticularBooks({
        parentUserId: input.parentUserId,
        marketId: input.marketId,
        selectedType: input.selectedType,
        runners: input.runners,
        marketType: input.marketType,
      }),
    enabled: Boolean(input.enabled && input.parentUserId && input.marketId),
    refetchInterval: input.enabled ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab PT ladder poll while open (~1.5s). */
export const PT_LADDER_POLL_MS = 1_500

/**
 * Lab `showRunners` → Aggregate `getLadderForGroup` / `getLadderTotalForGroup`.
 * Total Book + non-player uses Total ForGroup (player lt/gt flipped).
 */
export function useFancyPtLadder(input: {
  marketId: string
  userId: string
  accessLevel?: string
  selectedType: BookType
  nodeLevel: 'agent' | 'player'
  enabled: boolean
  /** Betfair line session → ladder body type betfair-line-sessions. */
  lineSession?: boolean
}) {
  const totalBook = fancyPtLadderUsesTotal(input.selectedType, input.nodeLevel)
  const lineSession = Boolean(input.lineSession)
  return useQuery({
    queryKey: [
      'aggregate',
      'workstation-pt-ladder',
      {
        marketId: input.marketId,
        userId: input.userId,
        accessLevel: input.accessLevel,
        selectedType: input.selectedType,
        totalBook,
        lineSession,
        forGroup: true,
      },
    ],
    queryFn: async () => {
      const body = buildFancyPtLadderBody({
        marketId: input.marketId,
        userId: input.userId,
        accessLevel: input.accessLevel,
        selectedType: input.selectedType,
        nodeLevel: input.nodeLevel,
        agentFallbackLevel: 'Master',
        lineSession,
      })
      const route = fancyPtLadderRoute(input.selectedType, input.nodeLevel, {
        forGroup: true,
      })
      return betFetch<GetLadderResponse>(`/v1/book/${route}`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    enabled: Boolean(input.enabled && input.marketId && input.userId),
    refetchInterval: input.enabled ? PT_LADDER_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab workstation `getBook` with marketId → `POST /v1/book/getBooksForBackend`. */
export function useWorkstationBooks(query: WorkstationQuery) {
  const { session, user, accessLevel, live } = useLiveIdentity()
  return useQuery({
    queryKey: keys.workstationBooks({
      marketId: query.marketId,
      selectedType: query.selectedType,
      user,
      level: accessLevel,
    }),
    queryFn: () => loadBooksForMarket(query, user!, accessLevel),
    enabled: Boolean(session && live && query.marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab `getMarketNetExposure` → `POST /v1/book/getMemberNetExposure`. */
export function useMemberNetExposure(marketId: string) {
  const { session, user, live } = useLiveIdentity()
  return useQuery({
    queryKey: keys.workstationMemberNe({ marketId, user }),
    queryFn: () => loadMemberNetExposure(marketId, user!),
    enabled: Boolean(session && live && marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab `getBetList` → `POST /v1/bets/getBetDetailsForWorkstation`. */
export function useBetDetailsForWorkstation(query: WorkstationQuery) {
  const { session, user, accessLevel, live } = useLiveIdentity()
  return useQuery({
    queryKey: keys.workstationBets({
      marketId: query.marketId,
      user,
      level: accessLevel,
      page: query.page ?? 1,
      maxRow: query.maxRow ?? 100,
      fromDate: query.fromDate,
      toDate: query.toDate,
      userName: query.userName,
      currencyType: query.currencyType,
    }),
    queryFn: () => loadBetDetails(query, user!, accessLevel),
    enabled: Boolean(session && live && query.marketId),
    refetchInterval: live ? BET_TICKER_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Compose Lab workstation payloads into one `WorkstationBook`.
 * Demo / offline → fixture. Live with empty APIs → null book (empty UI).
 */
export function useWorkstationBook(query: WorkstationQuery) {
  const { live, username, accessLevel } = useLiveIdentity()
  const totalQ = useTotalBooks1(query)
  const booksQ = useWorkstationBooks(query)
  const neQ = useMemberNetExposure(query.marketId)
  const betsQ = useBetDetailsForWorkstation(query)
  const ratesQ = useMarketRatesStream(live ? query.marketId : undefined)

  const fixture = useMemo(
    () => (query.marketId ? getWorkstationBook(query.marketId) : undefined),
    [query.marketId],
  )

  const book = useMemo((): WorkstationBook | null | undefined => {
    if (!live) return fixture ?? null
    const booksMarket = marketFromBooksGroups(booksQ.data, query.marketId)
    const assembled = assembleWorkstationBook({
      marketId: query.marketId,
      bookType: query.selectedType,
      total: totalQ.data,
      booksMarket,
      bets: betsQ.data,
      memberNe: neQ.data?.netExposure,
      fallback: {
        eventName: query.eventName || fixture?.eventName,
        marketName: query.marketName || fixture?.marketName,
        type: query.type || fixture?.type,
        username,
        accessLevel,
      },
    })
    if (!assembled) return assembled
    // Catalog HTTP seed (getTotalBooks1.liveRates) = Frontend Player-Markets
    // first paint. SSE overlays on top; fancy can still seed from open bets.
    let ratesPayload = ratesQ.payload
    const catalogRates = totalQ.data?.liveRates ?? null
    if (catalogRates) {
      ratesPayload = ratesPayload
        ? mergeLiveRatePayload(catalogRates, ratesPayload)
        : catalogRates
    }
    if (assembled.type === 'fancy') {
      const fromBets = fancyRatesFromBets(assembled.bets)
      if (fromBets) {
        ratesPayload = ratesPayload
          ? mergeLiveRatePayload(fromBets, ratesPayload)
          : fromBets
      }
    }
    if (!ratesPayload) return assembled
    return applyLiveRatesToBook(assembled, ratesPayload)
  }, [
    live,
    fixture,
    query.marketId,
    query.selectedType,
    query.eventName,
    query.marketName,
    query.type,
    totalQ.data,
    booksQ.data,
    betsQ.data,
    neQ.data?.netExposure,
    username,
    accessLevel,
    ratesQ.payload,
  ])

  const isLoading =
    live &&
    !totalQ.data &&
    !booksQ.data &&
    (totalQ.isLoading || booksQ.isLoading)

  const isError = live && totalQ.isError && booksQ.isError
  const error = totalQ.error ?? booksQ.error ?? betsQ.error ?? neQ.error
  const isFetching =
    totalQ.isFetching ||
    booksQ.isFetching ||
    betsQ.isFetching ||
    neQ.isFetching

  return {
    book: book === undefined ? null : book,
    memberNetExposure: live ? (neQ.data?.netExposure ?? null) : null,
    betCount: live ? (betsQ.data?.count ?? book?.bets.length ?? 0) : (book?.bets.length ?? 0),
    isLoading,
    isError,
    error,
    isFetching,
    live,
    refetch: () => {
      void totalQ.refetch()
      void booksQ.refetch()
      void neQ.refetch()
      void betsQ.refetch()
    },
    ratesStatus: ratesQ.status,
  }
}
